import { useEffect, useState } from "react";
import RightModal from "../components/templates/RightModal";
import { getCustomerInteractionsList, selectCustomerInteractionsData, selectCustomerInteractionsLoading } from "../redux/customerInteractions/customerInteractionsSlice";
import DynamicIcon from "../components/DynamicIcon";
import LoadingSection from "../components/LoadingSection";
import { selectAccountData } from "../redux/main/accountSlice";
import AddEditCustomerInteraction from "../components/customerInteractionsPage/AddEditCustomerInteraction";
import { resetRefreshNow, selectRightModalId, selectRightModalIsOpen, selectRightModalIsRefreshNow, selectRightModalmode, setModal } from "../redux/rightModalSlice";
import { useDispatch, useSelector } from "react-redux";
import { useColorPalette } from '../hooks/useColorPalette';
import DataTable from "../components/DataTable/DataTable";
import { formatDate } from "../utils/dateUtils";

export default function CustomerInteractionsList() {
    const dispatch = useDispatch();
    const palette = useColorPalette();
    const interactionsList = useSelector(selectCustomerInteractionsData);
    const interactionsLoading = useSelector(selectCustomerInteractionsLoading);
    const accountData = useSelector(selectAccountData);
    const token = localStorage.getItem('installamelo-auth-token');
    const [searchTerm, setSearchTerm] = useState('');
    const [dataTableKey, setDataTableKey] = useState(Date.now());
    
    // RightModal gestita tramite Redux
    const rightModalIsOpen = useSelector(selectRightModalIsOpen);
    const rightModalMode = useSelector(selectRightModalmode);
    const rightModalInteractionId = useSelector(selectRightModalId);
    
    // Gestione della paginazione con DataTable
    const handlePageChange = (pageIndex, pageSize) => {
        console.log('Cambio pagina a:', pageIndex, 'dimensione:', pageSize);
        const start = pageIndex * pageSize;
        
        dispatch(getCustomerInteractionsList({
            accountId: accountData?._id,
            token,
            search: searchTerm.length >= 3 ? searchTerm : '',
            start,
            length: pageSize,
            order: [{ column: 'creationDate', dir: 'desc' }] // Ordinamento per data di creazione decrescente
        }));
        dispatch(resetRefreshNow());
    };
    
    // Gestione della ricerca
    const handleSearch = (searchValue) => {
        console.log('Ricerca:', searchValue);
        setSearchTerm(searchValue);
        
        if (searchValue.length >= 3 || searchValue.length === 0) {
            dispatch(getCustomerInteractionsList({
                accountId: accountData?._id,
                token,
                search: searchValue.length >= 3 ? searchValue : '',
                start: 0,
                length: 10,
                order: [{ column: 'creationDate', dir: 'desc' }] // Ordinamento per data di creazione decrescente
            }));
            dispatch(resetRefreshNow());
        }
    };

    // Eseguo la chiamata iniziale quando il componente viene montato
    useEffect(() => {
        if (accountData?._id && token) {
            dispatch(getCustomerInteractionsList({ 
                accountId: accountData._id,
                token,
                search: searchTerm.length >= 3 ? searchTerm : '',
                start: 0,
                length: 10,
                order: [{ column: 'creationDate', dir: 'desc' }] // Ordinamento per data di creazione decrescente
            }));
            dispatch(resetRefreshNow());
            
            // Aggiorna la DataTable quando cambia il termine di ricerca
            if (searchTerm.length >= 3 || searchTerm === '') {
                setDataTableKey(Date.now());
            }
        }
    }, [dispatch, accountData, token, searchTerm]);
    

    
    // Aggiorna la tabella quando il modal viene chiuso
    useEffect(() => {
        if (!rightModalIsOpen && dataTableKey) {
            // Aggiorna la tabella
            setDataTableKey(Date.now());
        }
    }, [rightModalIsOpen]);
    
    // Avvio il refresh dei dati quando isRefreshNow della RightModal si attiva
    const rightModalIsRefreshNow = useSelector(selectRightModalIsRefreshNow);
    useEffect(() => {
        if (rightModalIsRefreshNow) handlePageChange(0, 10);
    }, [rightModalIsRefreshNow]);
    
    return (
        <div className="page">
            {interactionsLoading && <LoadingSection isFixed />}
            <h1><span className="font-light">Clienti / </span>Interazioni</h1>
            <div className="box">
                {/* Nuova interazione */}
                <div className="flex justify-end items-center gap-2 mb-4">
                    <button 
                        className="primary" 
                        onClick={() => dispatch(setModal({ mode: 'add' }))}
                    >
                        <DynamicIcon iconClass="fa-solid fa-plus" />
                        Nuova Interazione
                    </button>
                </div>

                {/* DataTable per le interazioni con i clienti */}
                <div className="mb-3">
                    <h3 className="text-lg font-semibold">Lista delle interazioni con clienti</h3>
                    <p className="text-sm text-gray-600">Visualizzazione ordinata per data (dal più recente al meno recente)</p>
                </div>
                <DataTable
                    key={dataTableKey}
                    columns={[
                        { 
                            id: 'date', 
                            title: 'DATA',
                            width: '15%',
                            render: (interaction) => (
                                <div className="text-left">
                                    {formatDate(interaction.creationDate) || '-'}
                                </div>
                            )
                        },
                        { 
                            id: 'code', 
                            title: 'CODICE',
                            width: '15%',
                            render: (interaction) => (
                                <div className="text-left">
                                    {interaction.code || '-'}
                                </div>
                            )
                        },
                        { 
                            id: 'customer', 
                            title: 'CLIENTE',
                            width: '15%',
                            render: (interaction) => (
                                <div className="text-left uppercase">
                                    {interaction.customerLabel || '-'}
                                </div>
                            )
                        },
                        { 
                            id: 'type', 
                            title: 'TIPO',
                            width: '15%',
                            render: (interaction) => (
                                <div className="text-left">
                                    {interaction.typeLabel || '-'}
                                </div>
                            )
                        },
                        { 
                            id: 'method', 
                            title: 'METODO',
                            width: '15%',
                            render: (interaction) => (
                                <div className="text-left">
                                    {interaction.interactionMethodTypeLabel || '-'}
                                </div>
                            )
                        },
                        { 
                            id: 'actions', 
                            title: 'OPZ',
                            width: '20%',
                            render: (interaction) => (
                                <div className="flex gap-3 justify-center">
                                    <button 
                                        style={{ color: 'inherit' }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            dispatch(setModal({
                                                mode: 'view',
                                                id: interaction._id || interaction.id
                                            }));
                                        }}
                                    >
                                        <DynamicIcon iconClass="fa-solid fa-eye" />
                                    </button>
                                    <button 
                                        style={{ color: 'inherit' }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            dispatch(setModal({
                                                mode: 'edit',
                                                id: interaction._id || interaction.id
                                            }));
                                        }}
                                    >
                                        <DynamicIcon iconClass="fa-solid fa-edit" />
                                    </button>
                                    <button 
                                        style={{ color: 'inherit' }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            // TODO: implementare la logica di eliminazione
                                            console.log('Eliminazione interazione:', interaction._id || interaction.id);
                                        }}
                                    >
                                        <DynamicIcon iconClass="fa-solid fa-trash" />
                                    </button>
                                </div>
                            )
                        }
                    ]}
                    data={interactionsList?.data || []}
                    loading={interactionsLoading}
                    totalRecords={interactionsList?.recordsFiltered || 0}
                    pageSize={10}
                    onPageChange={handlePageChange}
                    hasSearch={true}
                    onSearch={handleSearch}
                    initialSearchTerm={searchTerm}
                    emptyMessage="Nessuna interazione trovata"
                    renderCustomRow={(interaction, index, isEven, tableStyles) => {
                        // Funzione per gestire il click sulla riga
                        const handleRowClick = () => {
                            dispatch(setModal({
                                mode: 'view',
                                id: interaction._id || interaction.id
                            }));
                        };
                        
                        return (
                            <div 
                                key={interaction._id || interaction.id}
                                className="data-table-row cursor-pointer"
                                style={{
                                    backgroundColor: isEven ? tableStyles.evenRow.backgroundColor : tableStyles.oddRow.backgroundColor,
                                    color: isEven ? tableStyles.evenRow.color : tableStyles.oddRow.color,
                                }}
                                onClick={handleRowClick}
                            >
                                {/* Colonna DATA */}
                                <div className="data-table-cell" style={{ width: '15%' }} data-label="DATA">
                                    <div className="text-left">
                                        {formatDate(interaction.creationDate) || '-'}
                                    </div>
                                </div>
                                
                                {/* Colonna CODICE */}
                                <div className="data-table-cell" style={{ width: '15%' }} data-label="CODICE">
                                    <div className="text-left">
                                        {interaction.code || '-'}
                                    </div>
                                </div>
                                
                                {/* Colonna CLIENTE */}
                                <div className="data-table-cell" style={{ width: '15%' }} data-label="CLIENTE">
                                    <div className="text-left uppercase">
                                        {interaction.customerLabel || '-'}
                                    </div>
                                </div>
                                
                                {/* Colonna TIPO */}
                                <div className="data-table-cell" style={{ width: '15%' }} data-label="TIPO">
                                    <div className="text-left">
                                        {interaction.typeLabel || '-'}
                                    </div>
                                </div>
                                
                                {/* Colonna METODO */}
                                <div className="data-table-cell" style={{ width: '15%' }} data-label="METODO">
                                    <div className="text-left">
                                        {interaction.interactionMethodTypeLabel || '-'}
                                    </div>
                                </div>
                                
                                {/* Colonna AZIONI */}
                                <div className="data-table-cell" style={{ width: '25%' }} data-label="OPZ">
                                    <div className="flex gap-3 justify-center">
                                        {/* Icona visualizzazione */}
                                        <button 
                                            style={{ color: isEven ? tableStyles.evenRow.color : tableStyles.oddRow.color }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                dispatch(setModal({
                                                    mode: 'view',
                                                    id: interaction._id || interaction.id
                                                }));
                                            }}
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-eye" />
                                        </button>
                                        
                                        {/* Icona modifica */}
                                        <button 
                                            style={{ color: isEven ? tableStyles.evenRow.color : tableStyles.oddRow.color }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                dispatch(setModal({
                                                    mode: 'edit',
                                                    id: interaction._id || interaction.id
                                                }));
                                            }}
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-edit" />
                                        </button>
                                        
                                        {/* Icona eliminazione */}
                                        <button 
                                            style={{ color: isEven ? tableStyles.evenRow.color : tableStyles.oddRow.color }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                // TODO: implementare la logica di eliminazione
                                                console.log('Eliminazione interazione:', interaction._id || interaction.id);
                                            }}
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-trash" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    }}
                />
            </div>

            {/* RIGHT MODAL per inserimento o modifica interazione */}
            {
                rightModalIsOpen && (
                    <RightModal>
                        {(rightModalMode === 'add' || rightModalMode === 'edit') && (
                            <AddEditCustomerInteraction interactionId={rightModalInteractionId} />
                        )}
                        {
                            rightModalMode === 'view' && rightModalInteractionId && (
                                <div>{rightModalInteractionId}</div>
                            )
                        }
                    </RightModal>
                )
            }
        </div>
    );
}
